/**
 * How a voice deployment's scripted greeting reaches the caller.
 *
 *   - `instant` — the relay plays a saved recording of the greeting the moment
 *     the call connects, instead of waiting for the AI to produce it. The
 *     recording is keyed on everything that changes how it sounds, so a change
 *     to any of those is a new recording rather than a stale one.
 *   - `live`    — the AI says the greeting on every call, as it always has.
 *
 * Where it sits: the root of a TWILIO_VOICE deployment's settings, and the
 * `voiceSettings` of an EMBED or API deployment (`voice-config-sites.ts`).
 */

import { z } from "zod";

export const GREETING_PLAYBACK_MODES = ["instant", "live"] as const;

export const GreetingPlaybackSchema = z.enum(GREETING_PLAYBACK_MODES);

export type GreetingPlayback = z.infer<typeof GreetingPlaybackSchema>;

/**
 * The stored preference. Absent on every deployment written before the setting
 * existed, and on those it means {@link GREETING_PLAYBACK_FOR_UNSET_DEPLOYMENTS}.
 * Strict on the value: an unknown spelling is refused at the write rather than
 * stored and read as something nobody chose.
 */
export const GreetingPlaybackSettingSchema = GreetingPlaybackSchema.optional();

/** What a deployment born from now on is written with, by every client. */
export const DEFAULT_GREETING_PLAYBACK: GreetingPlayback = "instant";

/**
 * What a deployment that names no mode runs with. Every deployment that existed
 * before the setting names none, so this keeps them exactly as they were.
 */
export const GREETING_PLAYBACK_FOR_UNSET_DEPLOYMENTS: GreetingPlayback = "live";

/**
 * The mode a deployment's calls run with, from its stored preference. The one
 * place an absent or unrecognised value is given a mode, and it is always the
 * one that changes nothing: a value this reader cannot classify never turns a
 * recording on.
 */
export function resolveGreetingPlayback(stored: unknown): GreetingPlayback {
  const parsed = GreetingPlaybackSchema.safeParse(stored);
  return parsed.success ? parsed.data : GREETING_PLAYBACK_FOR_UNSET_DEPLOYMENTS;
}
